import React, { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from 'recharts';
import { categoricalColors, formatMoney, formatMoneyFull } from './chartConfig';
import { useAuth } from '../../../context/AuthContext';

export interface BarRow {
  label: string;
  amount: number;
  count?: number;
}

interface HorizontalBarListProps {
  rows: BarRow[];
  height?: number;
  color?: string;
  useCategoricalColors?: boolean;
}

/**
 * Left-labelled horizontal bars — used for top-N views (Top Submitters,
 * Expenses by Category, Fines by Category). Bars keep a consistent tone
 * unless `useCategoricalColors` is set, in which case each bar takes the
 * next slot from the shared categorical ramp.
 */
const HorizontalBarList: React.FC<HorizontalBarListProps> = ({
  rows,
  height,
  color,
  useCategoricalColors,
}) => {
  const theme = useTheme();
  const { currency } = useAuth();
  const palette = categoricalColors(theme);
  const single = color || theme.palette.primary.main;

  // Truncate long labels so the Y axis width stays reasonable.
  const truncate = (s: string, n = 20) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

  const data = useMemo(
    () =>
      rows.map((r, i) => ({
        ...r,
        display: truncate(r.label),
        fill: useCategoricalColors ? palette[i % palette.length] : single,
      })),
    [rows, palette, single, useCategoricalColors]
  );

  const computedHeight = height ?? Math.max(180, data.length * 36 + 40);

  if (rows.length === 0) {
    return (
      <Box sx={{ height: computedHeight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.disabled">No data</Typography>
      </Box>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={computedHeight}>
      <BarChart data={data} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 4" stroke={palette[0]} strokeOpacity={0.08} horizontal={false} />
        <XAxis
          type="number"
          tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(v) => formatMoney(v, currency)}
        />
        <YAxis
          type="category"
          dataKey="display"
          tick={{ fontSize: 12, fill: theme.palette.text.primary }}
          axisLine={false}
          tickLine={false}
          width={130}
        />
        <Tooltip
          wrapperStyle={{ zIndex: 1200, pointerEvents: 'none' }}
          contentStyle={{
            background: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 8,
            fontSize: 12,
            // Dark mode only — light mode uses recharts' default ink.
            ...(theme.palette.mode === 'dark' && { color: theme.palette.text.primary }),
            boxShadow: theme.palette.mode === 'dark'
              ? '0 6px 20px rgba(0,0,0,0.5)'
              : '0 4px 16px rgba(0,0,0,0.08)',
          }}
          itemStyle={theme.palette.mode === 'dark' ? { color: theme.palette.text.primary } : undefined}
          cursor={{ fill: theme.palette.action.hover }}
          formatter={(value: number) => [formatMoneyFull(value, currency), 'Amount']}
          labelFormatter={(l: string, payload: any) =>
            payload?.[0]?.payload?.label || l
          }
        />
        <Bar dataKey="amount" radius={[0, 6, 6, 0]} maxBarSize={22}>
          {data.map((d, i) => (
            <Cell key={i} fill={d.fill} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default HorizontalBarList;
